import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './baseQuery';

export interface IEventResponse {
  events_id: number;
  events_title: string;
  forms_id: number;
  point_rules_id: number;
  modals_id: number;
  banners_id: number;
  events_description: string;
  events_type: number;
  events_expire_type: string;
  events_point_expire_date: number;
  events_banner_prob: number;
  events_show_cost: number;
  events_member_type: number;
  events_target_birthday_from: number;
  events_target_birthday_to: number;
  events_target_gender: number;
  events_target_address: number;
  events_target_job: string;
  events_target_hobby: string;
  events_period_start: string;
  events_period_end: string;
  events_daily_start_time: string;
  events_daily_end_time: string;
  events_action_interval: number;
  events_limit_lottery_point_one_user_per_hour: number;
  events_limit_lottery_all_user_per_hour: number;
  events_suspended: number;
}

export interface IEventListResponse {
  banners_id: number;
  banners_image_url: string;
  banners_title: string;
  events_action_interval: number;
  events_banner_prob: number;
  events_daily_time_end: string;
  events_daily_time_start: string;
  events_description: string;
  events_expire_type: number;
  events_id: number;
  events_limit_lottery_all_user_per_hour: number;
  events_limit_lottery_point_one_user_per_hour: number;
  events_member_type: number;
  events_period_end: string;
  events_period_start: string;
  events_point_expire_date: number;
  events_show_cost: number;
  events_suspended: number;
  events_target_address: number;
  events_target_birthday_from: number;
  events_target_birthday_to: number;
  events_target_gender: number;
  events_target_hobby: string;
  events_target_job: string;
  events_title: string;
  events_type: number;
  events_hashtag: string;
  forms_id: number;
  forms_title: string;
  gacha_details_id: number;
  gacha_details_lottery_flag: number;
  gacha_details_point_amount: number;
  gacha_details_point_percent: number;
  gacha_details_prize_num: number;
  gacha_details_result_image_url: string;
  invitations_id: number;
  invitations_inv_need_days: number;
  invitations_inv_number: number;
  invitations_inv_repeat_flag: number;
  invitations_invited_user_point: number;
  invitation_details: IInvitationDetails[];
  modals_id: number;
  modals_image_url: string;
  modals_title: string;
  point_rules_id: number;
  point_rules_title: string;
}

export interface IInvitationDetails {
  invitation_no: number;
  inv_point_amount: number;
}

export interface IFormModalBannerPoint {
  banners_id: number[];
  banners_image_url: string[];
  banners_title: string[];
  forms_id: number[];
  forms_title: string[];
  modals_id: number[];
  modals_image_url: string[];
  modals_title: string[];
  point_rules_id: number[];
  point_rules_title: string[];
}

// Define a service using a base URL and expected endpoints
export const eventApi = createApi({
  reducerPath: 'eventApi',
  tagTypes: ['event', 'upload', 'eventList', 'formModalBannerPoint'],
  baseQuery: axiosBaseQuery(),
  endpoints: builder => ({
    getAllEvent: builder.query<IEventResponse[], string>({
      query: params => ({
        url: `/events${params && '?' + params}`,
        method: 'GET',
      }),
      providesTags: ['event', 'eventList'],
      transformResponse: (
        response: { data_response: IEventResponse[] },
        meta,
        arg,
      ) => response.data_response,
    }),
    getFormModalBannerPoint: builder.query<IFormModalBannerPoint, void>({
      query: () => ({
        url: `/events/event_lists`,
        method: 'GET',
      }),
      providesTags: ['formModalBannerPoint'],
      transformResponse: (response: IFormModalBannerPoint) => response,
    }),
    getEventList: builder.query<
      IEventListResponse,
      { id: number | string; params: string }
    >({
      query: payload => ({
        url: `/events/event_lists/${payload.id}${
          payload.params && '?' + payload.params
        }`,
        method: 'GET',
      }),
      providesTags: ['eventList'],
      transformResponse: (response: IEventListResponse) => response,
    }),
    createEvent: builder.mutation<any, any>({
      query(body) {
        return {
          url: '/events',
          method: 'POST',
          data: body,
        };
      },
      invalidatesTags: ['event'],
    }),
    deleteEvent: builder.mutation<any, string>({
      query(id) {
        return {
          url: `/events/${id}`,
          method: 'Delete',
        };
      },
      invalidatesTags: ['event'],
    }),
    updateEvent: builder.mutation<any, any>({
      query({ id, body }) {
        return {
          url: `/events/${id}`,
          method: 'PUT',
          data: body,
        };
      },
      invalidatesTags: ['event', 'eventList'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllEventQuery,
  useCreateEventMutation,
  useDeleteEventMutation,
  useUpdateEventMutation,
  useGetEventListQuery,
  useGetFormModalBannerPointQuery,
} = eventApi;
